import styled from '@emotion/styled';
import Button from 'antd/es/button';
import Checkbox from 'antd/es/checkbox/Checkbox';
import Popconfirm from 'antd/es/popconfirm';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { StoreInfo } from 'services/api/store';
import { MAX_SELECTED_LENGTH } from '../hooks/useStoreSelector';

interface Props {
  onClearAllFollow: () => void;
  selected: StoreInfo[];
  onChange: (item: StoreInfo[], status: boolean) => void;
  currentTab: 'all' | 'follower' | General.UpperCaseRegion;
  currentTabStoreList: StoreInfo[];
  followList: StoreInfo[];
  platform?: General.PlatformUpperCaseTunnel;
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  padding-left: 8px;
  margin-left: 8px;
  margin-right: 8px;
  height: 40px;
  align-items: center;
  position: relative;
  box-shadow: 0 2px 2px -1px rgb(168 179 205 / 20%);
  .ant-popover-content {
    width: 240px;
  }
  .clean-follow-btn {
    padding-left: 8px;
    padding-right: 8px;
    color: var(--fett-text-tertiary);
    &:active,
    &:hover {
      color: #336fff;
    }
  }
  .tips {
    font-size: 12px;
    align-self: center;
    color: var(--fett-text-quaternary);
  }
`;

const StoreListHeader = React.memo((props: Props) => {
  const {
    onClearAllFollow,
    selected,
    onChange,
    currentTabStoreList,
    currentTab,
    followList,
    platform,
  } = props;
  const { t } = useTranslate();
  const [checked, setChecked] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const toggleSelectAll = useCallback(
    (status: boolean) => {
      onChange(currentTabStoreList, status);
      setChecked(status);
    },
    [currentTabStoreList, onChange],
  );
  const currentTabSelected = useMemo(() => {
    if (currentTab === 'all') {
      return selected;
    }
    if (currentTab === 'follower') {
      return selected.filter((x) => followList.find((y) => y.storeId === x.storeId));
    }
    return selected.filter((x) => {
      if (platform) {
        return x.externalType === platform && x.region === currentTab;
      }
      return x.region === currentTab;
    });
  }, [currentTab, selected, followList, platform]);
  useEffect(() => {
    if (!selected.length) {
      setChecked(false);
      return;
    }
    const isSelectedAll = currentTabStoreList
      .slice(0, MAX_SELECTED_LENGTH)
      .every((x) => selected.find((y) => y.storeId === x.storeId));
    setChecked(isSelectedAll);
  }, [currentTabStoreList, selected]);
  return (
    <Container ref={ref}>
      <div>
        <Checkbox
          onChange={(e) => toggleSelectAll(e.target.checked)}
          checked={checked}
          disabled={!currentTabStoreList.length}
        >
          {t('storeSelector.全选')}
        </Checkbox>
        {currentTabSelected.length > 0 ? (
          <span className="tips">
            {t('storeSelector.已选{x}', { x: currentTabSelected.length })}
          </span>
        ) : null}
      </div>
      {currentTab === 'follower' ? (
        <Popconfirm
          title={t('storeSelector.清空关注确认提示')}
          onConfirm={onClearAllFollow}
          getTooltipContainer={() => ref.current || document.body}
        >
          <Button type="link" className="clean-follow-btn">
            {t('storeSelector.清空')}
          </Button>
        </Popconfirm>
      ) : null}
    </Container>
  );
});

export default StoreListHeader;
